// ComplexOrderFields.tsx
import React from "react";
import { useTheme } from "../../../hooks/useTheme";
import { CategoryFieldType } from "../../../types/enums";
import { CategoryFieldsGroup } from "../useComplexCartModal";
import styles from "./ComplexOrderFields.module.scss";

interface FieldValue {
  fieldId: number;
  value: string;
}

interface ComplexOrderFieldsProps {
  fieldsByCategory: CategoryFieldsGroup[];
  values: FieldValue[];
  onChange: (fieldId: number, value: string) => void;
}

export default function ComplexOrderFields({
  fieldsByCategory,
  values,
  onChange,
}: ComplexOrderFieldsProps) {
  const { theme } = useTheme();

  const getValue = (fieldId: number): string => {
    return values.find((v) => v.fieldId === fieldId)?.value || "";
  };

  const renderInput = (field: any) => {
    const value = getValue(field.id);
    const disabled = !field.isFillByClient;

    switch (field.type) {
      case CategoryFieldType.NUMBER:
        return (
          <input
            type="number"
            className={styles.input}
            value={value}
            onChange={(e) => onChange(field.id, e.target.value)}
            placeholder={field.name}
            disabled={disabled}
            required={field.required}
            style={{
              backgroundColor: theme.background,
              color: theme.text,
              borderColor: theme.border,
            }}
          />
        );

      case CategoryFieldType.TEXT:
        return (
          <textarea
            className={styles.textarea}
            value={value}
            onChange={(e) => onChange(field.id, e.target.value)}
            placeholder={field.name}
            disabled={disabled}
            required={field.required}
            rows={3}
            style={{
              backgroundColor: theme.background,
              color: theme.text,
              borderColor: theme.border,
            }}
          />
        );

      case CategoryFieldType.DATE:
        return (
          <input
            type="date"
            className={styles.input}
            value={value}
            onChange={(e) => onChange(field.id, e.target.value)}
            disabled={disabled}
            required={field.required}
            style={{
              backgroundColor: theme.background,
              color: theme.text,
              borderColor: theme.border,
            }}
          />
        );

      case CategoryFieldType.BOOLEAN:
        return (
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={value === "true"}
              onChange={(e) =>
                onChange(field.id, e.target.checked ? "true" : "false")
              }
              disabled={disabled}
            />
            <span className={styles.checkboxText} style={{ color: theme.text }}>
              {field.name}
            </span>
          </label>
        );

      case CategoryFieldType.STRING:
      default:
        return (
          <input
            type="text"
            className={styles.input}
            value={value}
            onChange={(e) => onChange(field.id, e.target.value)}
            placeholder={field.name}
            disabled={disabled}
            required={field.required}
            style={{
              backgroundColor: theme.background,
              color: theme.text,
              borderColor: theme.border,
            }}
          />
        );
    }
  };

  if (fieldsByCategory.length === 0) {
    return (
      <div className={styles.container}>
        <h2 className={styles.title} style={{ color: theme.text }}>
          Детали заказа
        </h2>
        <p className={styles.emptyText} style={{ color: theme.textSecondary }}>
          Дополнительные детали не требуются
        </p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h2 className={styles.title} style={{ color: theme.text }}>
        Детали заказа
      </h2>

      {fieldsByCategory.map((group) => (
        <div key={group.categoryId} className={styles.categoryGroup}>
          <h3 className={styles.categoryTitle} style={{ color: theme.primary }}>
            Заполните для категории «{group.categoryName}»
          </h3>
          <div className={styles.fieldsList}>
            {group.fields
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((field) => (
                <div key={field.id} className={styles.fieldItem}>
                  {field.type !== CategoryFieldType.BOOLEAN && (
                    <label
                      className={styles.label}
                      style={{ color: theme.textSecondary }}
                    >
                      {field.name}
                      {field.required && (
                        <span style={{ color: theme.danger }}> *</span>
                      )}
                    </label>
                  )}
                  {renderInput(field)}
                  {field.description && (
                    <span
                      className={styles.hint}
                      style={{ color: theme.textSecondary }}
                    >
                      {field.description}
                    </span>
                  )}
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}
